import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/modules/auth'
import { useVehicles } from '@/modules/vehicles'
import { OnboardingWizard } from '@/modules/onboarding'
import { CreateGearInventoryDto } from '@/modules/gear'
import { VehicleApi, AuthApi, CreateVehicleProfileDto as ApiCreateVehicleProfileDto } from '@/shared/api'

interface MinimalVehicleInput {
  make: string
  model: string
  year: number
}

export default function OnboardingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, getAccessToken } = useAuth()
  const { vehicles } = useVehicles()
  const [isLoading, setIsLoading] = useState(false)

  // Check if this is coming from vehicle management (adding additional vehicle)
  const isAddingVehicle = location.state?.fromVehicleManagement || false
  // Allow re-entering onboarding for testing: /onboarding?force=1
  const forceOnboarding = new URLSearchParams(location.search).get('force') === '1'

  const hasCompletedOnboarding = user?.onboarding_completed_at != null

  // Skip onboarding if the user has already completed/dismissed it, or already has vehicles
  useEffect(() => {
    if (!user || isAddingVehicle || forceOnboarding) return
    if (hasCompletedOnboarding || (vehicles && vehicles.length > 0)) {
      navigate('/dashboard')
    }
  }, [user, isAddingVehicle, forceOnboarding, hasCompletedOnboarding, vehicles, navigate])

  // Create vehicle and auth API instances
  const vehicleApi = useMemo(() => new VehicleApi(getAccessToken), [getAccessToken])
  const authApi = useMemo(() => new AuthApi(getAccessToken), [getAccessToken])

  const markOnboardingComplete = async () => {
    try {
      await authApi.markOnboardingComplete()
    } catch {
      // Non-fatal: don't block navigation if this fails
    }
  }

  const handleComplete = async (vehicleProfile: MinimalVehicleInput, _gearItems: CreateGearInventoryDto[]) => {
    setIsLoading(true)
    try {
      const apiVehicleProfile: ApiCreateVehicleProfileDto = {
        name: `${vehicleProfile.year} ${vehicleProfile.make} ${vehicleProfile.model}`,
        vehicle_type: 'other',
        make: vehicleProfile.make,
        model: vehicleProfile.model,
        year: vehicleProfile.year,
        is_primary: vehicles.length === 0,
        is_daily_driver: true,
      }
      await vehicleApi.createVehicle(apiVehicleProfile)
      await markOnboardingComplete()
      navigate('/dashboard')
    } catch (error) {
      alert('Failed to save vehicle profile. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkip = async () => {
    await markOnboardingComplete()
    if (isAddingVehicle) {
      if (user?.id) {
        navigate(`/users/${user.id}`)
      } else {
        navigate('/dashboard')
      }
    } else {
      navigate('/dashboard')
    }
  }

  if (!user) {
    navigate('/login')
    return null
  }

  return (
    <OnboardingWizard
      onComplete={handleComplete}
      onSkip={handleSkip}
      vehicleApi={vehicleApi}
    />
  )
}
